<template>
  <div class="control-bar">
    <!-- Mic -->
    <v-tooltip :text="audioMuted ? 'Unmute' : 'Mute'" location="top">
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="ctrl-btn"
          :class="{ 'ctrl-btn--off': audioMuted }"
          @click="$emit('toggle-audio')"
        >
          <v-icon size="22">{{ audioMuted ? 'mdi-microphone-off' : 'mdi-microphone' }}</v-icon>
          <span class="ctrl-btn__label">{{ audioMuted ? 'Unmute' : 'Mute' }}</span>
        </button>
      </template>
    </v-tooltip>

    <!-- Camera -->
    <v-tooltip :text="videoMuted ? 'Start video' : 'Stop video'" location="top">
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="ctrl-btn"
          :class="{ 'ctrl-btn--off': videoMuted }"
          @click="$emit('toggle-video')"
        >
          <v-icon size="22">{{ videoMuted ? 'mdi-video-off' : 'mdi-video' }}</v-icon>
          <span class="ctrl-btn__label">{{ videoMuted ? 'Start video' : 'Stop video' }}</span>
        </button>
      </template>
    </v-tooltip>

    <!-- Screen share -->
    <v-tooltip :text="isScreenSharing ? 'Stop sharing' : 'Share screen'" location="top">
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="ctrl-btn"
          :class="{ 'ctrl-btn--active': isScreenSharing }"
          @click="$emit('toggle-screen-share')"
        >
          <v-icon size="22">mdi-monitor-share</v-icon>
          <span class="ctrl-btn__label">{{ isScreenSharing ? 'Stop share' : 'Share' }}</span>
        </button>
      </template>
    </v-tooltip>

    <!-- Recording (admin only) -->
    <v-tooltip v-if="isAdmin" :text="isRecording ? 'Stop recording' : 'Record'" location="top">
      <template #activator="{ props }">
        <button
          v-bind="props"
          class="ctrl-btn"
          :class="{ 'ctrl-btn--recording': isRecording }"
          @click="$emit('toggle-recording')"
        >
          <v-icon size="22">{{ isRecording ? 'mdi-record-circle' : 'mdi-record-circle-outline' }}</v-icon>
          <span class="ctrl-btn__label">{{ isRecording ? 'Stop rec' : 'Record' }}</span>
        </button>
      </template>
    </v-tooltip>

    <!-- Speaker selection -->
    <v-menu v-if="supportsSpeakerSelection" location="top">
      <template #activator="{ props }">
        <button v-bind="props" class="ctrl-btn">
          <v-icon size="22">mdi-volume-high</v-icon>
          <span class="ctrl-btn__label">Speaker</span>
        </button>
      </template>
      <v-list density="compact" bg-color="#2a2a2a" min-width="220">
        <v-list-subheader>Speaker</v-list-subheader>
        <v-list-item
          v-for="device in audioOutputs"
          :key="device.deviceId"
          :title="device.label"
          :active="device.deviceId === speakerDeviceId"
          @click="$emit('change-speaker', device.deviceId)"
        />
      </v-list>
    </v-menu>

    <div class="control-bar__spacer" />

    <!-- Leave / End meeting -->
    <template v-if="isAdmin">
      <v-menu location="top">
        <template #activator="{ props }">
          <button v-bind="props" class="ctrl-btn ctrl-btn--leave">
            <v-icon size="22">mdi-phone-hangup</v-icon>
            <span class="ctrl-btn__label">Leave</span>
            <v-icon size="14" class="ml-1">mdi-chevron-up</v-icon>
          </button>
        </template>
        <v-list density="compact" bg-color="#2a2a2a">
          <v-list-item
            prepend-icon="mdi-logout"
            title="Leave call"
            @click="$emit('leave')"
          />
          <v-list-item
            prepend-icon="mdi-close-circle"
            title="End meeting for all"
            class="text-error"
            @click="$emit('end-meeting')"
          />
        </v-list>
      </v-menu>
    </template>
    <template v-else>
      <button class="ctrl-btn ctrl-btn--leave" @click="$emit('leave')">
        <v-icon size="22">mdi-phone-hangup</v-icon>
        <span class="ctrl-btn__label">Leave</span>
      </button>
    </template>
  </div>
</template>

<script>
import speakerDeviceMixin from '../../mixins/speakerDeviceMixin';

export default {
  name: 'ControlBar',

  mixins: [speakerDeviceMixin],

  props: {
    audioMuted: { type: Boolean, default: false },
    videoMuted: { type: Boolean, default: false },
    isScreenSharing: { type: Boolean, default: false },
    isRecording: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    speakerDeviceId: { type: String, default: null },
  },

  emits: [
    'toggle-audio',
    'toggle-video',
    'toggle-screen-share',
    'toggle-recording',
    'leave',
    'end-meeting',
    'change-speaker',
  ],

  mounted() {
    this.refreshOutputDevices();
  },
};
</script>

<style scoped>
.control-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 16px;
  height: 72px;
  background: #1e1e1e;
  border-top: 1px solid #333;
  flex-shrink: 0;
}

.control-bar__spacer {
  flex: 1;
}

.ctrl-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 64px;
  height: 52px;
  padding: 6px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #e0e0e0;
  cursor: pointer;
  position: relative;
  transition: background 0.15s ease, color 0.15s ease;
  font-family: inherit;
}

.ctrl-btn:hover {
  background: #333;
}

.ctrl-btn--off {
  background: #b00020;
  color: #fff;
}

.ctrl-btn--off:hover {
  background: #c62828;
}

.ctrl-btn--active {
  background: #1565c0;
  color: #fff;
}

.ctrl-btn--active:hover {
  background: #1976d2;
}

.ctrl-btn--recording {
  color: #ef5350;
}

.ctrl-btn--leave {
  background: #b00020;
  color: #fff;
  min-width: 72px;
}

.ctrl-btn--leave:hover {
  background: #c62828;
}

.ctrl-btn__label {
  font-size: 0.65rem;
  line-height: 1;
  white-space: nowrap;
}

.ctrl-btn__badge {
  position: absolute;
  top: 6px;
  right: 8px;
  background: #1976d2;
  color: #fff;
  border-radius: 10px;
  font-size: 0.6rem;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
}

.ctrl-btn__badge--amber {
  background: #ef6c00;
}
</style>
